import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from './apiAuth';
import { authHeader, canonicalizeResponse } from './common';

// Register API
export const register = createAsyncThunk(
  'auth/register',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', payload);
      const { user, accessToken, refreshToken } = response.data.data;
      
      if (accessToken) {
        localStorage.setItem("token", JSON.stringify(accessToken));
        localStorage.setItem("refreshToken", JSON.stringify(refreshToken));
        localStorage.setItem("user", JSON.stringify(user));
        return { user, accessToken, refreshToken };
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

// Login API
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const { user, accessToken, refreshToken } = response.data.data;
      
      if (accessToken) {
        localStorage.setItem("token", JSON.stringify(accessToken));
        localStorage.setItem("refreshToken", JSON.stringify(refreshToken));
        localStorage.setItem("user", JSON.stringify(user));
        return { user, accessToken, refreshToken };
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const socialLogin = createAsyncThunk(
  'auth/socialLogin',
  async ({ provider, data, ip_data, referralCode }, { rejectWithValue }) => {
    try {
      // Get referral code from session storage if not provided
      const referrerCode = referralCode || sessionStorage.getItem("referrerCode") || "";
      const payload = { 
        provider: provider, 
        data: data, 
        ip_details: ip_data,
        referralCode: referrerCode
      };
      const response = await api.post('/auth/social', payload);
      const { user, accessToken, refreshToken } = response.data.data;
      
      if (accessToken) {
        localStorage.setItem("token", JSON.stringify(accessToken));
        localStorage.setItem("refreshToken", JSON.stringify(refreshToken));
        localStorage.setItem("user", JSON.stringify(user));
        return { user, accessToken, refreshToken };
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Social login failed');
    }
  }
);

export const fetchMe = createAsyncThunk(
  "auth/fetchMe",
   async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/users/profile`, {
        headers: await authHeader(),
      });
     return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMe = createAsyncThunk(
  "auth/updateMe",
  async (updatedMe, { rejectWithValue }) => {
    try {
      const response = await api.put('/users/profile', updatedMe, {
        headers: await authHeader(),
      });
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  "auth/deleteAccount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete('/users/account', {
        headers: await authHeader(),
      });
     return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk("/auth/logout", async () => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  return;
});

const getUserFromStorage = () => {
  try {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    localStorage.removeItem("user");
    return null;
  }
};

const authSlice = createSlice({
  name: "auth",
  initialState: {
    isLoggedIn: !!localStorage.getItem("user"),
    user: getUserFromStorage(),
    personalDetail: {},
    status: "idle",
    error: null,
  },
   reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.status = "idle";
      state.error = null;
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user"); 
    },
    setTokens: (state, action) => {
      const { token } = action.payload;
      state.token = token;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register cases
      .addCase(register.pending, (state) => {
        state.status = "loading";
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        const { user, accessToken } = action.payload || {};
        if (user && accessToken) {
          state.status = "succeeded";
          state.user = user;
          state.token = accessToken;
        } else {
          state.status = "failed";
          state.error = "Unexpected response structure";
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Login cases
      .addCase(login.pending, (state) => {
        state.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        const { user, accessToken } = action.payload || {};
        if (user && accessToken) {
          state.status = "succeeded";
          state.user = user;
          state.token = accessToken;
        } else {
          state.status = "failed";
          state.error = "Unexpected response structure";
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
   
      // login with social accounts
      .addCase(socialLogin.pending, (state) => {
        state.status = "loading";
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        const { user, accessToken } = action.payload || {};

        if (user && accessToken) {
          state.status = "succeeded";
          state.user = user;
          state.token = accessToken;
        } else {
          state.status = "failed";
          state.error = "Unexpected response structure";
        }
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(logout.fulfilled, (state) => {
         localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        state.isLoggedIn = false;
        state.user = null;
        state.token = null;
       
      })
      // Add fetchMe cases
      .addCase(fetchMe.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.personalDetail = action.payload.data;
      })
      .addCase(fetchMe.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Add updateMe cases
      .addCase(updateMe.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateMe.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.personalDetail = action.payload?.data ?? action.payload;
      })
      .addCase(updateMe.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Add deleteAccount cases
      .addCase(deleteAccount.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.status = "succeeded";
        state.isLoggedIn = false;
        state.user = null;
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});
export const { logout: logoutAction, setTokens } = authSlice.actions;

export default authSlice.reducer;

